import { AdjustmentsIcon, CogIcon, PencilIcon } from "@heroicons/react/outline";
import { BracesIcon, SaveIcon } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";

import blocks, { BlockType } from "../../..";
import {
  InlineEditorSection,
  ModeToggle,
  PromptMode,
  SaveToRegistryDialog,
} from "../../../InlineTemplateEditor";

import ApplyInputVariablesToggle from "./ApplyInputVariablesToggle";
import ChatHistorySource from "./ChatHistorySource";
import EngineConfiguration from "./EngineConfiguration";
import MockToolResponsesToggle from "./MockToolResponsesToggle";
import PromptEditorModal from "./PromptEditorModal";
import ReturnTemplateOnlyToggle from "./ReturnTemplateOnlyToggle";
import TemplateSelector from "./TemplateSelector";
import VerboseModeToggle from "./VerboseModeToggle";

import { isEvalLikeEnv, useBlockEnv } from "@/components/PowerBlock/Block/env-context";
import InputVariablesField from "@/components/PowerBlock/Block/InputVariablesField";
import { useIsReadonly } from "@/components/PowerBlock/Block/readonly-context";
import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import { SourceType } from "@/components/PowerBlock/Block/SourcesField/SelectSource";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { ToastType } from "@/enums";
import { Entity, EntityType, useCreatePromptTemplate } from "@/queries";
import { PromptBlueprint } from "@/types";
import { InlineTemplate } from "@/types/evaluate";
import { displayErrorToast, displayToast } from "@/utils/toast";

interface SaveCompleteResult {
  id: number;
  name: string;
  version?: number;
}

interface ConfigurationViewProps {
  mode: PromptMode;
  onModeChange: (mode: PromptMode) => void;
  templateName?: string;
  templateId?: number;
  promptId?: number;
  isLoading: boolean;
  isRefetching: boolean;
  promptData?: Partial<PromptBlueprint>;
  defaultPromptData?: Partial<PromptBlueprint>;
  isChat: boolean;
  inputVariables: string[];
  sources: Record<SourceType, string[]>;
  parseInputVariablesIsLoading: boolean;
  showVerboseMode: boolean;
  verboseEnabled: boolean;
  returnTemplateOnlyEnabled: boolean;
  hydrateInputVariablesEnabled: boolean;
  templateFormat?: "f-string" | "jinja2";
  onTemplateSelection: (entity: Entity<EntityType.PROMPT | EntityType.SNIPPET>) => void;
  onEngineUpdate: (promptData?: Partial<PromptBlueprint>) => void;
  onVerboseChange: (enabled: boolean) => void;
  onAutofill: (mappings: Record<string, string>) => void;
  onReturnTemplateOnlyChange: (enabled: boolean) => void;
  onHydrateInputVariablesChange: (enabled: boolean) => void;
  chatHistorySource?: string;
  onChatHistoryToggle: (enabled: boolean) => void;
  onChatHistorySourceChange: (source: string) => void;
  mockToolResponsesEnabled: boolean;
  mockScenario?: string | null;
  mockStopTools?: string | null;
  onMockToolResponsesToggle: (enabled: boolean) => void;
  onMockScenarioChange: (scenario: string) => void;
  onMockStopToolsChange: (tools: string) => void;
  currentVersionNumber?: number;
  currentLabel?: string;
  folderId: number | null;
  releaseLabelExists?: boolean;
  isCheckingLabel?: boolean;
  promptError?: unknown;
  messages?: string[];
  inlineTemplate?: InlineTemplate;
  onInlineTemplateChange: (template: InlineTemplate) => void;
  forceOpenVariableSelector?: string;
}

const ConfigurationView = React.memo(
  ({
    mode,
    onModeChange,
    templateName,
    promptId,
    isLoading,
    promptData,
    defaultPromptData,
    isChat,
    inputVariables,
    sources,
    parseInputVariablesIsLoading,
    showVerboseMode,
    verboseEnabled,
    returnTemplateOnlyEnabled,
    hydrateInputVariablesEnabled,
    templateFormat,
    onTemplateSelection,
    onEngineUpdate,
    onVerboseChange,
    onReturnTemplateOnlyChange,
    onHydrateInputVariablesChange,
    chatHistorySource,
    onChatHistoryToggle,
    onChatHistorySourceChange,
    mockToolResponsesEnabled,
    mockScenario,
    mockStopTools,
    onMockToolResponsesToggle,
    onMockScenarioChange,
    onMockStopToolsChange,
    onAutofill,
    currentVersionNumber,
    folderId,
    currentLabel,
    releaseLabelExists,
    isCheckingLabel,
    promptError,
    messages,
    inlineTemplate,
    onInlineTemplateChange,
    forceOpenVariableSelector,
  }: ConfigurationViewProps) => {
    const user = useUser();
    const workspaceId = user?.activeWorkspaceId;
    const isReadonly = useIsReadonly();
    const userToken = useAuth()?.userToken;
    // Mocked agent runner is an eval/smart-table concept only (not workflow nodes) and is
    // meaningless when the column just returns the rendered template without running it.
    const showMockToolResponses = isEvalLikeEnv(useBlockEnv()) && !returnTemplateOnlyEnabled;

    const [modalState, setModalState] = useState<{
      isOpen: boolean;
      mode: "create" | "edit";
    }>({ isOpen: false, mode: "create" });

    const [saveDialogOpen, setSaveDialogOpen] = useState(false);

    const createPromptMutation = useCreatePromptTemplate(userToken || "");

    const TemplateIcon = useMemo(() => blocks[BlockType.PROMPT_TEMPLATE].icon!, []);

    const handleEditPrompt = () => {
      setModalState({ isOpen: true, mode: "edit" });
    };

    const handleSaveComplete = useCallback(
      (result: SaveCompleteResult) => {
        onTemplateSelection({
          id: result.id,
          name: result.name,
          version_number: result.version,
        } as Entity<EntityType.PROMPT | EntityType.SNIPPET>);
        setModalState(prev => ({ ...prev, isOpen: false }));
      },
      [onTemplateSelection],
    );

    const handleModalClose = useCallback(() => {
      setModalState(prev => ({ ...prev, isOpen: false }));
    }, []);

    const handleSaveToRegistry = useCallback(
      (name: string) =>
        new Promise<(() => void) | void>((resolve, reject) => {
          if (!inlineTemplate?.prompt_template || !workspaceId) {
            resolve();
            return;
          }

          createPromptMutation.mutate(
            {
              prompt_template: {
                prompt_name: name,
                workspace_id: workspaceId,
              },
              prompt_version: {
                prompt_template: inlineTemplate.prompt_template,
                metadata: inlineTemplate.metadata || undefined,
                provider_base_url_name: inlineTemplate.provider_base_url_name || null,
                provider_id: inlineTemplate.provider_id || undefined,
              },
            },
            {
              onSuccess: data => {
                if ("success" in data && !data.success) {
                  displayErrorToast("Failed to save prompt template");
                  reject(new Error("Failed to save prompt template"));
                  return;
                }

                displayToast("Prompt saved to registry", ToastType.success);

                // Switch to registry mode and select the new prompt
                const promptVersion = "prompt_version" in data ? data.prompt_version : null;
                resolve(() => {
                  if (promptVersion) {
                    onTemplateSelection({
                      id: promptVersion.prompt_id,
                      name: name,
                      version_number: promptVersion.version_number,
                    } as Entity<EntityType.PROMPT>);
                    onModeChange("registry");
                  }
                  setSaveDialogOpen(false);
                });
              },
              onError: () => {
                displayErrorToast("Failed to save prompt template");
                reject(new Error("Failed to save prompt template"));
              },
            },
          );
        }),
      [inlineTemplate, workspaceId, createPromptMutation, onTemplateSelection, onModeChange],
    );

    const hasLabelError = currentLabel && !isCheckingLabel && releaseLabelExists === false;
    const hasPromptError = Boolean(promptError);

    const getErrorMessage = (error: unknown): string => {
      if (error && typeof error === "object" && "message" in error) {
        const { message } = error as { message: unknown };
        if (typeof message === "string") {
          return message;
        }
      }
      return "Failed to load prompt.";
    };

    const canSaveInline = mode === "inline" && inlineTemplate?.prompt_template && !isReadonly;

    return (
      <div className="space-y-4">
        {/* Template Section with Mode Toggle */}
        <SectionGroup
          title={mode === "inline" ? "Inline Template" : "Template Selection"}
          description={
            mode === "inline"
              ? "Define your prompt template directly"
              : "Choose and configure your prompt template"
          }
          icon={
            mode === "inline" ? (
              <PencilIcon className="h-4 w-4" />
            ) : (
              <TemplateIcon className="h-4 w-4" />
            )
          }
          headerAction={
            !isReadonly && (
              <div className="flex items-center gap-2">
                {canSaveInline && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSaveDialogOpen(true)}
                    className="gap-1.5 text-xs"
                  >
                    <SaveIcon className="h-3.5 w-3.5" />
                    Save to Registry
                  </Button>
                )}
                <ModeToggle mode={mode} onModeChange={onModeChange} disabled={isReadonly} />
              </div>
            )
          }
        >
          <div className="space-y-4">
            {mode === "registry" ? (
              <TemplateSelector
                template={
                  promptId
                    ? {
                        id: promptId,
                        name: templateName!,
                        folder_id: folderId ?? undefined,
                        version_number: currentVersionNumber,
                        label: currentLabel,
                      }
                    : undefined
                }
                onSelect={onTemplateSelection}
                isLoading={isLoading}
                onEditPrompt={handleEditPrompt}
                hasError={hasLabelError || hasPromptError}
                errorMessage={
                  hasLabelError ? `Release label not found` : getErrorMessage(promptError)
                }
              />
            ) : (
              <InlineEditorSection
                template={inlineTemplate}
                onTemplateChange={onInlineTemplateChange}
                readonly={isReadonly}
              />
            )}
          </div>
        </SectionGroup>

        {/* Model Configuration Section - shown for registry mode when not return_template_only */}
        {mode === "registry" && !returnTemplateOnlyEnabled && (templateName || promptData) && (
          <SectionGroup
            title="Model Configuration"
            description="Configure model parameters and settings"
            variant="default"
            icon={<CogIcon className="h-4 w-4" />}
          >
            <EngineConfiguration
              promptData={promptData}
              defaultPromptData={defaultPromptData}
              isLoading={isLoading && !promptData}
              onUpdate={onEngineUpdate}
              isChat={isChat}
              messages={messages}
            />
          </SectionGroup>
        )}

        {/* Input Variables Section */}
        {(!returnTemplateOnlyEnabled ||
          (returnTemplateOnlyEnabled && hydrateInputVariablesEnabled)) &&
          inputVariables.length > 0 && (
            <div data-smart-table-tour-sources-dependencies="true">
              <SectionGroup
                title="Input Variables"
                description="Map template variables to data sources"
                variant="default"
                icon={<BracesIcon className="h-4 w-4" />}
              >
                <InputVariablesField
                  variables={inputVariables}
                  sources={sources}
                  sourceKeyPrefix="prompt_template_variable_mappings"
                  isLoading={parseInputVariablesIsLoading}
                  showAutofill
                  onAutofill={onAutofill}
                  templateFormat={templateFormat}
                  forceOpenVariableSelector={forceOpenVariableSelector}
                />
              </SectionGroup>
            </div>
          )}

        {/* Advanced Settings Section */}
        {(showVerboseMode ||
          returnTemplateOnlyEnabled ||
          showMockToolResponses ||
          (isChat && chatHistorySource !== undefined)) && (
          <SectionGroup
            title="Advanced Settings"
            variant="compact"
            collapsible
            defaultExpanded={
              returnTemplateOnlyEnabled ||
              mockToolResponsesEnabled ||
              (isChat && chatHistorySource !== undefined)
            }
            icon={<AdjustmentsIcon className="h-4 w-4" />}
          >
            <div className="space-y-4">
              <ReturnTemplateOnlyToggle
                enabled={returnTemplateOnlyEnabled}
                onChange={onReturnTemplateOnlyChange}
              />
              {returnTemplateOnlyEnabled && (
                <ApplyInputVariablesToggle
                  enabled={hydrateInputVariablesEnabled}
                  onChange={onHydrateInputVariablesChange}
                />
              )}
              {showVerboseMode && !returnTemplateOnlyEnabled && (
                <VerboseModeToggle
                  enabled={verboseEnabled}
                  onChange={onVerboseChange}
                  showVerboseMode={showVerboseMode}
                />
              )}
              {!returnTemplateOnlyEnabled && isChat && (
                <ChatHistorySource
                  enabled={chatHistorySource !== undefined}
                  source={chatHistorySource}
                  sources={sources}
                  onToggle={onChatHistoryToggle}
                  onSourceChange={onChatHistorySourceChange}
                />
              )}
              {showMockToolResponses && (
                <MockToolResponsesToggle
                  enabled={mockToolResponsesEnabled}
                  scenario={mockScenario}
                  stopTools={mockStopTools}
                  onToggle={onMockToolResponsesToggle}
                  onScenarioChange={onMockScenarioChange}
                  onStopToolsChange={onMockStopToolsChange}
                />
              )}
            </div>
          </SectionGroup>
        )}

        {/* Modal for editing registry prompts */}
        <PromptEditorModal
          isOpen={modalState.isOpen}
          onClose={handleModalClose}
          mode={modalState.mode}
          workspaceId={workspaceId!}
          existingPromptId={modalState.mode === "edit" ? promptId : undefined}
          existingPromptName={modalState.mode === "edit" ? templateName : undefined}
          existingVersionNumber={modalState.mode === "edit" ? currentVersionNumber : undefined}
          existingLabel={modalState.mode === "edit" ? currentLabel : undefined}
          onSaveComplete={handleSaveComplete}
        />

        {/* Dialog for saving inline template to registry */}
        <SaveToRegistryDialog
          isOpen={saveDialogOpen}
          onClose={() => setSaveDialogOpen(false)}
          onSave={handleSaveToRegistry}
        />
      </div>
    );
  },
  (prevProps, nextProps) => {
    const keysToCompare = [
      "mode",
      "templateName",
      "templateId",
      "promptId",
      "isLoading",
      "isRefetching",
      "isChat",
      "showVerboseMode",
      "verboseEnabled",
      "returnTemplateOnlyEnabled",
      "hydrateInputVariablesEnabled",
      "mockToolResponsesEnabled",
      "mockScenario",
      "mockStopTools",
      "chatHistorySource",
      "parseInputVariablesIsLoading",
      "templateFormat",
      "forceOpenVariableSelector",
    ] as const;

    for (const key of keysToCompare) {
      if (prevProps[key] !== nextProps[key]) {
        return false;
      }
    }

    // Deep comparison for complex objects
    if (JSON.stringify(prevProps.promptData) !== JSON.stringify(nextProps.promptData)) {
      return false;
    }
    if (
      JSON.stringify(prevProps.defaultPromptData) !== JSON.stringify(nextProps.defaultPromptData)
    ) {
      return false;
    }
    if (JSON.stringify(prevProps.inputVariables) !== JSON.stringify(nextProps.inputVariables)) {
      return false;
    }
    if (JSON.stringify(prevProps.sources) !== JSON.stringify(nextProps.sources)) {
      return false;
    }
    if (JSON.stringify(prevProps.inlineTemplate) !== JSON.stringify(nextProps.inlineTemplate)) {
      return false;
    }
    if (
      prevProps.releaseLabelExists !== nextProps.releaseLabelExists ||
      prevProps.isCheckingLabel !== nextProps.isCheckingLabel ||
      prevProps.promptError !== nextProps.promptError
    ) {
      return false;
    }

    return true;
  },
);

ConfigurationView.displayName = "ConfigurationView";

export default ConfigurationView;
